import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

type ButtonSize = 'sm' | 'md';

interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  busy?: boolean;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
};

const ring = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1';

export const PrimaryButton = forwardRef<HTMLButtonElement, BaseButtonProps>(function PrimaryButton(
  { size = 'md', leftIcon, rightIcon, busy = false, className, children, disabled, style, ...rest },
  ref,
): JSX.Element {
  const baseBg = style?.backgroundColor ?? 'var(--bui-accent)';
  const hoverBg =
    style?.backgroundColor && style.backgroundColor !== 'var(--bui-accent)'
      ? (style.backgroundColor as string)
      : 'var(--bui-accent-hover)';
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || busy}
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        sizeClasses[size],
        ring,
        className,
      )}
      style={{
        backgroundColor: baseBg,
        color: 'var(--bui-accent-on)',
        borderRadius: 'var(--bui-radius-control, 10px)',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled && !busy) e.currentTarget.style.backgroundColor = hoverBg;
        rest.onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = baseBg as string;
        rest.onMouseLeave?.(e);
      }}
      {...rest}
    >
      {leftIcon}
      <span>{busy ? 'Working…' : children}</span>
      {rightIcon}
    </button>
  );
});

export const SecondaryButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  function SecondaryButton(
    { size = 'md', leftIcon, rightIcon, busy = false, className, children, disabled, ...rest },
    ref,
  ): JSX.Element {
    return (
      <button
        ref={ref}
        type={rest.type ?? 'button'}
        disabled={disabled || busy}
        className={clsx(
          'inline-flex items-center justify-center gap-2 border font-medium transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-60',
          sizeClasses[size],
          ring,
          className,
        )}
        style={{
          backgroundColor: 'var(--bui-surface)',
          color: 'var(--bui-text-primary)',
          borderColor: 'var(--bui-border)',
          borderRadius: 'var(--bui-radius-control, 10px)',
        }}
        {...rest}
      >
        {leftIcon}
        <span>{busy ? 'Working…' : children}</span>
        {rightIcon}
      </button>
    );
  },
);

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  active?: boolean;
  size?: number;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, active = false, size = 32, className, children, ...rest },
  ref,
): JSX.Element {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={clsx(
        'inline-flex items-center justify-center border transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        ring,
        className,
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: active ? 'var(--bui-accent)' : 'var(--bui-surface)',
        color: active ? 'var(--bui-accent-on)' : 'var(--bui-text-secondary)',
        borderColor: active ? 'var(--bui-accent)' : 'var(--bui-border)',
        borderRadius: 'var(--bui-radius-control, 10px)',
      }}
      {...rest}
    >
      {children}
    </button>
  );
});
